import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick } from '../config/gtm';

interface NavbarProps {
    currentTheme?: number;
    onThemeChange?: (theme: number) => void;
}

type NavItem =
    | {
          type: 'link';
          label: string;
          path: string;
          activePrefix?: string;
          isContact?: boolean;
      }
    | {
          type: 'dropdown';
          label: string;
          children: { label: string; path: string }[];
      };

const WavyBottom = () => (
    <svg
        className="absolute bottom-0 left-0 w-full h-3 text-princonser-dark pointer-events-none"
        viewBox="0 0 1440 24"
        preserveAspectRatio="none"
        aria-hidden
    >
        <path
            fill="currentColor"
            d="M0,12 C240,24 480,0 720,12 C960,24 1200,0 1440,12 L1440,24 L0,24 Z"
        />
    </svg>
);

const Navbar: React.FC<NavbarProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
    const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
    const { language, setLanguage } = useLanguage();

    const t = (es: string, en: string) => (language === 'es' ? es : en);

    const isHomePage = location.pathname === '/';
    const isTransparent = isHomePage;

    const aboutPaths = ['/sobre-nosotros', '/que-es-princonser', '/blog-princonser'];

    const navItems: NavItem[] = [
        { type: 'link', label: t('Home', 'Home'), path: '/' },
        {
            type: 'dropdown',
            label: t('Sobre nosotros', 'About Us'),
            children: [
                {
                    label: t('¿Qué es Princonser Kids?', 'What is Princonser Kids?'),
                    path: '/sobre-nosotros',
                },
                {
                    label: t('Blog Princonser', 'Princonser Blog'),
                    path: '/blog-princonser',
                },
            ],
        },
        { type: 'link', label: t('Libros', 'Books'), path: '/libros/1', activePrefix: '/libros' },
        { type: 'link', label: t('Círculo de sabios', 'Circle of Sages'), path: '/consejo-sabios' },
        {
            type: 'link',
            label: t('Para familias y escuelas', 'For Families and Schools'),
            path: '/support-materials',
        },
        { type: 'link', label: t('Contacto', 'Contact'), path: '/#contacto', isContact: true },
    ];

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsAboutDropdownOpen(false);
        setIsMobileAboutOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    const isAboutSectionActive = () => aboutPaths.some((p) => location.pathname === p);

    const isLinkActive = (path: string, activePrefix?: string) => {
        if (path === '/') return location.pathname === '/';
        if (path === '/#contacto') return false;
        if (activePrefix) return location.pathname.startsWith(activePrefix);
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const goTo = (path: string, isContact?: boolean) => {
        if (isContact) {
            if (location.pathname === '/') {
                document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' });
            } else {
                navigate('/#contacto');
            }
            return;
        }
        navigate(path);
    };

    const linkClass = (active: boolean) =>
        `relative px-1.5 xl:px-2 py-1 text-xs xl:text-sm font-medium transition-colors hover:text-princonser-green whitespace-nowrap ${
            active ? 'text-white' : 'text-white/90'
        }`;

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
                isTransparent
                    ? 'bg-transparent'
                    : 'bg-princonser-purple-dark/97 backdrop-blur-md shadow-lg shadow-princonser-purple/20'
            }`}
        >
            <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-[4.5rem]">
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="flex-shrink-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-princonser-green rounded-lg"
                        aria-label="Princonser Kids Home"
                    >
                        <span className="block leading-tight select-none">
                            <span
                                className="text-lg sm:text-xl font-extrabold tracking-tight"
                                style={{
                                    color: '#EBE9FC',
                                    WebkitTextStroke: '1.5px rgba(255,255,255,0.85)',
                                    paintOrder: 'stroke fill',
                                }}
                            >
                                PRINCONSER
                            </span>
                            <span className="block text-base sm:text-lg font-extrabold -mt-0.5">
                                <span className="text-princonser-purple-light">K</span>
                                <span className="text-sky-300">i</span>
                                <span className="text-princonser-green-bright">d</span>
                                <span className="text-princonser-pink">s</span>
                            </span>
                        </span>
                    </button>

                    {/* Desktop nav */}
                    <div className="hidden lg:flex items-center gap-0.5 xl:gap-1.5">
                        {navItems.map((item) => {
                            if (item.type === 'link') {
                                const active = isLinkActive(item.path, item.activePrefix);
                                return (
                                    <button
                                        key={item.path}
                                        type="button"
                                        onClick={() => goTo(item.path, item.isContact)}
                                        className={linkClass(active)}
                                    >
                                        {item.label}
                                        {active && (
                                            <span className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full bg-princonser-green" />
                                        )}
                                    </button>
                                );
                            }

                            const aboutActive = isAboutSectionActive();
                            return (
                                <div
                                    key="about-dropdown"
                                    className="relative"
                                    onMouseEnter={() => setIsAboutDropdownOpen(true)}
                                    onMouseLeave={() => setIsAboutDropdownOpen(false)}
                                >
                                    <button
                                        type="button"
                                        className={`${linkClass(aboutActive)} inline-flex items-center gap-1`}
                                        aria-expanded={isAboutDropdownOpen}
                                        aria-haspopup="true"
                                    >
                                        {item.label}
                                        <svg
                                            className={`w-3.5 h-3.5 transition-transform ${isAboutDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2.5}
                                            aria-hidden
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                        {aboutActive && (
                                            <span className="absolute -bottom-0.5 left-1 right-1 h-0.5 rounded-full bg-princonser-green" />
                                        )}
                                    </button>

                                    {isAboutDropdownOpen && (
                                        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-princonser-purple-muted py-2 z-50 overflow-hidden">
                                            {item.children.map((child) => {
                                                const childActive = isLinkActive(child.path);
                                                return (
                                                    <button
                                                        key={child.path}
                                                        type="button"
                                                        onClick={() => {
                                                            navigate(child.path);
                                                            setIsAboutDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                                                            childActive
                                                                ? 'bg-princonser-purple-muted text-princonser-purple'
                                                                : 'text-princonser-navy hover:bg-princonser-purple-muted/60'
                                                        }`}
                                                    >
                                                        {child.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop actions */}
                    <div className="hidden lg:flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                            className="px-2 py-1 text-xs font-semibold text-white/80 hover:text-white border border-white/25 rounded-full transition-colors"
                            title={language === 'es' ? 'English' : 'Español'}
                        >
                            {language === 'es' ? 'EN' : 'ES'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                trackCTAClick('Registrarse', 'Navbar', 'button');
                                navigate('/register');
                            }}
                            className="px-5 py-2.5 rounded-full text-sm font-bold text-princonser-navy bg-princonser-green hover:bg-princonser-green-hover shadow-princonser-green transition-all hover:scale-[1.02]"
                        >
                            {t('Registrarse', 'Register')}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                trackCTAClick('Iniciar sesión', 'Navbar', 'button');
                                navigate('/login');
                            }}
                            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white border-2 border-white/90 hover:bg-white/10 transition-all"
                        >
                            {t('Iniciar sesión', 'Log in')}
                        </button>
                    </div>

                    {/* Mobile toggle */}
                    <div className="lg:hidden flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                            className="px-2 py-1 text-xs font-bold text-white border border-white/30 rounded-full"
                        >
                            {language === 'es' ? 'EN' : 'ES'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 text-white rounded-lg hover:bg-white/10"
                            aria-expanded={isMobileMenuOpen}
                            aria-label={t('Menú', 'Menu')}
                        >
                            <div className="w-6 h-5 flex flex-col justify-between">
                                <span
                                    className={`block h-0.5 w-full bg-white rounded transition-transform ${
                                        isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                                    }`}
                                />
                                <span
                                    className={`block h-0.5 w-full bg-white rounded transition-opacity ${
                                        isMobileMenuOpen ? 'opacity-0' : ''
                                    }`}
                                />
                                <span
                                    className={`block h-0.5 w-full bg-white rounded transition-transform ${
                                        isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                                    }`}
                                />
                            </div>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu */}
            {isMobileMenuOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-hidden
                    />
                    <div className="fixed top-16 left-0 right-0 z-50 lg:hidden bg-princonser-purple-dark border-t border-princonser-purple/40 max-h-[calc(100vh-4rem)] overflow-y-auto shadow-2xl">
                        <div className="px-4 py-6 space-y-1">
                            {navItems.map((item) => {
                                if (item.type === 'link') {
                                    const active = isLinkActive(item.path, item.activePrefix);
                                    return (
                                        <button
                                            key={item.path}
                                            type="button"
                                            onClick={() => {
                                                goTo(item.path, item.isContact);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                                                active
                                                    ? 'bg-princonser-purple/50 text-princonser-green'
                                                    : 'text-white/90 hover:bg-white/5'
                                            }`}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                }

                                return (
                                    <div key="about-dropdown-mobile">
                                        <button
                                            type="button"
                                            onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                                                isAboutSectionActive()
                                                    ? 'bg-princonser-purple/50 text-princonser-green'
                                                    : 'text-white/90 hover:bg-white/5'
                                            }`}
                                        >
                                            {item.label}
                                            <svg
                                                className={`w-4 h-4 transition-transform ${isMobileAboutOpen ? 'rotate-180' : ''}`}
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {isMobileAboutOpen && (
                                            <div className="ml-3 mt-1 space-y-1 border-l-2 border-princonser-purple/40 pl-3">
                                                {item.children.map((child) => {
                                                    const childActive = isLinkActive(child.path);
                                                    return (
                                                        <button
                                                            key={child.path}
                                                            type="button"
                                                            onClick={() => {
                                                                navigate(child.path);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                                                childActive
                                                                    ? 'bg-princonser-purple/50 text-princonser-green'
                                                                    : 'text-white/85 hover:bg-white/5'
                                                            }`}
                                                        >
                                                            {child.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div className="pt-4 mt-4 border-t border-princonser-purple/40 flex flex-col gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        trackCTAClick('Registrarse Mobile', 'Navbar', 'button');
                                        navigate('/register');
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="w-full py-3 rounded-full font-bold text-princonser-navy bg-princonser-green"
                                >
                                    {t('Registrarse', 'Register')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        trackCTAClick('Iniciar sesión Mobile', 'Navbar', 'button');
                                        navigate('/login');
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="w-full py-3 rounded-full font-semibold text-white border-2 border-white/80"
                                >
                                    {t('Iniciar sesión', 'Log in')}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {!isTransparent && <WavyBottom />}
        </header>
    );
};

export default Navbar;
